import * as React from 'react';

import type { NotificationItemDto } from '@/api/notifications-client';
import { StatusBadge } from '@/components/ui/status-badge';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  categoryMeta,
  colorCodeToTone,
  formatNotificationTime,
  isUnread,
} from '@/lib/notification-presentation';

// =============================================================================
// notification-item-button.tsx — Ligne cliquable d'une notification (R15-COM-06).
// =============================================================================
// Partagée par le volet consolidé (NotificationPanel) et le widget D-04 (NotificationCenterWidget).
// Un <button> (navigation clavier native) : icône de type (catégorie) + indicateur COULEUR (tone DS via
// colorCode, G-5) + titre (libellé FR de la catégorie, qui DOUBLE la couleur — WCAG 1.4.1) + horodatage +
// état lu/non-lu (libellé texte « Non lu », jamais une couleur seule). Aucune couleur hex en dur.

export interface NotificationItemButtonProps {
  readonly item: NotificationItemDto;
  /** Activation (clic/Entrée) : l'appelant marque lu puis navigue (résolution via payloadContext). */
  readonly onActivate: (item: NotificationItemDto) => void;
}

export function NotificationItemButton({
  item,
  onActivate,
}: NotificationItemButtonProps): React.JSX.Element {
  const meta = categoryMeta(item.category);
  const tone = colorCodeToTone(item.colorCode);
  const unread = isUnread(item);
  const time = formatNotificationTime(item.triggeredAt);
  const Icon = meta.Icon;

  return (
    <button
      type="button"
      onClick={() => onActivate(item)}
      className={cn(
        'flex w-full items-start gap-3 rounded-md border border-transparent px-3 py-2 text-left',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        unread ? 'bg-accent/40' : 'bg-transparent',
      )}
    >
      <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          {/* Couleur (tone DS) DOUBLÉE par le libellé de catégorie (l'unique porteur d'info, WCAG 1.4.1). */}
          <StatusBadge tone={tone}>{meta.label}</StatusBadge>
          {unread ? (
            <Badge variant="outline" className="text-[11px]">
              Non lu
            </Badge>
          ) : null}
        </span>
        {time.length > 0 ? (
          <span className="text-xs text-muted-foreground">{time}</span>
        ) : null}
      </span>
    </button>
  );
}
